import React, { useState, memo } from 'react';
import { Wand2, BookOpen, Sparkles, CheckCircle, Clock, Palette, Play, ArrowRight, Zap, Film, Gamepad2, Map, Lock } from 'lucide-react';
type Story = {
  id: number;
  thumbnail: string;
  title: string;
  date: string;
  duration: string;
  summary: string;
  estimatedPages?: number;
  estimatedCost?: number;
};
const stories: Story[] = [{
  id: 1,
  thumbnail: 'https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=400&h=400&fit=crop',
  title: 'greek mythos',
  date: 'December 2, 2025',
  duration: '3:47',
  summary: 'It sounds like you told a fascinating story about Achilles, a man destined for heroism...',
  estimatedPages: 8,
  estimatedCost: 5
}, {
  id: 2,
  thumbnail: 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=400&h=400&fit=crop',
  title: 'nautilus',
  date: 'December 1, 2025',
  duration: '0:52',
  summary: 'extraordinary adventure! In August of 1931, you were aboard a submarine...',
  estimatedPages: 6,
  estimatedCost: 4
}, {
  id: 3,
  thumbnail: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=400&fit=crop',
  title: 'PB trip',
  date: 'November 30, 2025',
  duration: '3:32',
  summary: "Chris's obsession with Fletcher's Farm Goods peanut butter led you, Christina, and Jeff...",
  estimatedPages: 8,
  estimatedCost: 5
}, {
  id: 4,
  thumbnail: 'https://images.unsplash.com/photo-1473186578172-c141e6798cf4?w=400&h=400&fit=crop',
  title: 'sadasda',
  date: 'November 29, 2025',
  duration: '0:33',
  summary: 'Here are a few options for expanding your summary, keeping the humor and fantastical elements...',
  estimatedPages: 6,
  estimatedCost: 4
}, {
  id: 5,
  thumbnail: 'https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=400&h=400&fit=crop',
  title: 'Testing story',
  date: 'November 21, 2025',
  duration: '1:00',
  summary: 'A wonderful test story capturing precious family moments and memories to preserve for generations...',
  estimatedPages: 6,
  estimatedCost: 4
}];
type CreatePageProps = {
  onStartStorybookCreation?: (storyId: number) => void;
};
type CreationType = 'storybook' | 'movie' | 'game' | 'quest';
export function CreatePage({
  onStartStorybookCreation
}: CreatePageProps) {
  const [selectedType, setSelectedType] = useState<CreationType>('storybook');
  const creationTypes = [{
    id: 'storybook' as const,
    name: 'Storybooks',
    icon: BookOpen,
    description: 'AI-illustrated books',
    color: 'purple',
    available: true
  }, {
    id: 'movie' as const,
    name: 'Movies',
    icon: Film,
    description: 'Animated videos',
    color: 'blue',
    available: false
  }, {
    id: 'game' as const,
    name: 'Games',
    icon: Gamepad2,
    description: 'Interactive experiences',
    color: 'green',
    available: false
  }, {
    id: 'quest' as const,
    name: 'Quests',
    icon: Map,
    description: 'Adventure journeys',
    color: 'amber',
    available: false
  }];
  return <div className="flex-1 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen" data-id="element-470">
      <div className="px-8 py-8" data-id="element-471">
        {/* Creation Type Selector */}
        <div className="mb-8" data-id="element-472">
          <h1 className="text-4xl font-bold text-gray-900 mb-2" data-id="element-473">Create</h1>
          <p className="text-gray-600 text-lg mb-6" data-id="element-474">
            Transform your stories into amazing creations
          </p>

          <div className="grid grid-cols-4 gap-4" data-id="element-475">
            {creationTypes.map(type => {
            const Icon = type.icon;
            const isSelected = selectedType === type.id;
            const isAvailable = type.available;
            return <button key={type.id} onClick={() => isAvailable && setSelectedType(type.id)} disabled={!isAvailable} className={`relative p-6 rounded-2xl border-2 transition-all ${isAvailable ? isSelected ? `border-${type.color}-500 bg-${type.color}-50 shadow-lg` : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md' : 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'}`} data-id="element-476">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${isAvailable ? isSelected ? `bg-${type.color}-500` : `bg-${type.color}-100` : 'bg-gray-200'}`} data-id="element-477">
                    <Icon className={`w-6 h-6 ${isAvailable ? isSelected ? 'text-white' : `text-${type.color}-600` : 'text-gray-400'}`} data-id="element-478" />
                  </div>
                  <div className="text-left" data-id="element-479">
                    <div className="font-semibold text-gray-900 mb-1" data-id="element-480">
                      {type.name}
                    </div>
                    <div className="text-sm text-gray-600" data-id="element-481">
                      {type.description}
                    </div>
                  </div>

                  {!isAvailable && <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 bg-gray-200 rounded-full" data-id="element-482">
                      <Lock className="w-3 h-3 text-gray-600" data-id="element-483" />
                      <span className="text-xs font-medium text-gray-600" data-id="element-484">
                        Soon
                      </span>
                    </div>}

                  {isSelected && isAvailable && <div className="absolute top-3 right-3" data-id="element-485">
                      <CheckCircle className={`w-5 h-5 text-${type.color}-500`} data-id="element-486" />
                    </div>}
                </button>;
          })}
          </div>
        </div>

        {/* Storybook Content */}
        {selectedType === 'storybook' && <>
            {/* Split Hero */}
            <div className="grid grid-cols-2 gap-8 mb-8" data-id="element-487">
              {/* Left: Info */}
              <div className="bg-white rounded-3xl p-10 shadow-sm" data-id="element-488">
                <div className="flex items-center gap-2 mb-4" data-id="element-489">
                  <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center" data-id="element-490">
                    <Wand2 className="w-5 h-5 text-purple-600" data-id="element-491" />
                  </div>
                  <span className="text-purple-600 font-semibold" data-id="element-492">
                    AI Creation Studio
                  </span>
                </div>
                <h2 className="text-5xl font-bold text-gray-900 mb-4" data-id="element-493">
                  Create Your Storybook
                </h2>
                <p className="text-gray-600 text-lg mb-8" data-id="element-494">
                  Transform your recorded stories into beautifully illustrated
                  books with AI-powered generation
                </p>

                <div className="space-y-4" data-id="element-495">
                  <div className="flex items-start gap-3" data-id="element-496">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-497">
                      <CheckCircle className="w-4 h-4 text-green-600" data-id="element-498" />
                    </div>
                    <div data-id="element-499">
                      <div className="font-semibold text-gray-900" data-id="element-500">
                        Professional Quality
                      </div>
                      <div className="text-sm text-gray-600" data-id="element-501">
                        AI-generated illustrations in multiple art styles
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3" data-id="element-502">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-503">
                      <CheckCircle className="w-4 h-4 text-green-600" data-id="element-504" />
                    </div>
                    <div data-id="element-505">
                      <div className="font-semibold text-gray-900" data-id="element-506">
                        Fast Generation
                      </div>
                      <div className="text-sm text-gray-600" data-id="element-507">
                        Your book ready in approximately 5 minutes
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3" data-id="element-508">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-509">
                      <CheckCircle className="w-4 h-4 text-green-600" data-id="element-510" />
                    </div>
                    <div data-id="element-511">
                      <div className="font-semibold text-gray-900" data-id="element-512">
                        Fully Customizable
                      </div>
                      <div className="text-sm text-gray-600" data-id="element-513">
                        Edit pages, regenerate images, adjust text
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: Preview */}
              <div className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-3xl p-10 flex items-center justify-center relative overflow-hidden" data-id="element-514">
                <div className="absolute inset-0 bg-white/10 backdrop-blur-3xl" data-id="element-515"></div>
                <div className="relative z-10 text-center" data-id="element-516">
                  <div className="w-48 h-64 bg-white rounded-2xl shadow-2xl mx-auto mb-6 overflow-hidden" data-id="element-517">
                    <img src="https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?w=400&h=600&fit=crop" alt="Example book" className="w-full h-full object-cover" data-id="element-518" />
                  </div>
                  <div className="text-white text-lg font-medium" data-id="element-519">
                    Example Storybook
                  </div>
                  <div className="text-white/80 text-sm" data-id="element-520">
                    Created in 4 minutes
                  </div>
                </div>
              </div>
            </div>

            {/* Process Steps */}
            <div className="bg-white rounded-2xl p-8 mb-8 shadow-sm" data-id="element-521">
              <h3 className="text-xl font-bold text-gray-900 mb-6" data-id="element-522">
                How It Works
              </h3>
              <div className="grid grid-cols-4 gap-6" data-id="element-523">
                {[{
              step: 1,
              title: 'Select Story',
              desc: 'Choose from your recorded stories',
              icon: BookOpen
            }, {
              step: 2,
              title: 'Choose Style',
              desc: 'Pick an art style and format',
              icon: Palette
            }, {
              step: 3,
              title: 'AI Generation',
              desc: 'Watch as AI creates your book',
              icon: Sparkles
            }, {
              step: 4,
              title: 'Review & Share',
              desc: 'Edit, download, or print',
              icon: CheckCircle
            }].map(item => <div key={item.step} className="text-center" data-id="element-524">
                    <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4" data-id="element-525">
                      <item.icon className="w-8 h-8 text-purple-600" data-id="element-526" />
                    </div>
                    <div className="text-sm font-semibold text-gray-900 mb-1" data-id="element-527">
                      {item.title}
                    </div>
                    <div className="text-xs text-gray-600" data-id="element-528">{item.desc}</div>
                  </div>)}
              </div>
            </div>

            {/* Stories - Card View with Preview */}
            <div data-id="element-529">
              <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-530">
                Select a Story
              </h2>
              <div className="grid grid-cols-1 gap-6" data-id="element-531">
                {stories.map((story, index) => <div key={story.id} data-tour={index === 0 ? 'story-card' : undefined} className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all" data-id="element-532">
                    <div className="flex" data-id="element-533">
                      {/* Left: Story Info */}
                      <div className="flex-1 p-8" data-id="element-534">
                        <div className="flex items-start justify-between mb-4" data-id="element-535">
                          <div data-id="element-536">
                            <h3 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-537">
                              {story.title}
                            </h3>
                            <div className="flex items-center gap-4 text-sm text-gray-600" data-id="element-538">
                              <span data-id="element-539">{story.date}</span>
                              <span data-id="element-540">•</span>
                              <div className="flex items-center gap-1" data-id="element-541">
                                <Clock className="w-4 h-4" data-id="element-542" />
                                {story.duration}
                              </div>
                            </div>
                          </div>
                        </div>

                        <p className="text-gray-700 mb-6 line-clamp-3" data-id="element-543">
                          {story.summary}
                        </p>

                        {/* Estimated Output */}
                        <div className="grid grid-cols-3 gap-4 mb-6" data-id="element-544">
                          <div className="p-4 bg-gray-50 rounded-xl" data-id="element-545">
                            <div className="text-sm text-gray-600 mb-1" data-id="element-546">
                              Estimated Pages
                            </div>
                            <div className="text-2xl font-bold text-gray-900" data-id="element-547">
                              {story.estimatedPages}
                            </div>
                          </div>
                          <div className="p-4 bg-gray-50 rounded-xl" data-id="element-548">
                            <div className="text-sm text-gray-600 mb-1" data-id="element-549">
                              Generation Time
                            </div>
                            <div className="text-2xl font-bold text-gray-900" data-id="element-550">
                              ~5m
                            </div>
                          </div>
                          <div className="p-4 bg-green-50 rounded-xl" data-id="element-551">
                            <div className="text-sm text-gray-600 mb-1" data-id="element-552">
                              Cost
                            </div>
                            <div className="text-2xl font-bold text-green-600" data-id="element-553">
                              {story.estimatedCost} credits
                            </div>
                          </div>
                        </div>

                        {/* CTA */}
                        <button onClick={() => onStartStorybookCreation?.(story.id)} className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all shadow-lg" data-id="element-554">
                          <Wand2 className="w-5 h-5" data-id="element-555" />
                          Start Creating
                          <ArrowRight className="w-5 h-5" data-id="element-556" />
                        </button>
                      </div>

                      {/* Right: Visual Preview */}
                      <div className="w-80 bg-gradient-to-br from-purple-50 to-pink-50 p-8 flex items-center justify-center" data-id="element-557">
                        <div className="relative" data-id="element-558">
                          <div className="w-48 h-64 bg-white rounded-xl shadow-xl overflow-hidden transform rotate-3 hover:rotate-0 transition-transform" data-id="element-559">
                            <img src={story.thumbnail} alt={story.title} className="w-full h-full object-cover" data-id="element-560" />
                          </div>
                          <div className="absolute -bottom-4 -right-4 w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center shadow-lg" data-id="element-561">
                            <Play className="w-6 h-6 text-white ml-0.5" data-id="element-562" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
          </>}

        {/* Coming Soon States for Other Types */}
        {selectedType !== 'storybook' && <div className="bg-white rounded-3xl p-16 shadow-sm text-center" data-id="element-563">
            <div className="w-24 h-24 bg-gray-100 rounded-3xl flex items-center justify-center mx-auto mb-6" data-id="element-564">
              {selectedType === 'movie' && <Film className="w-12 h-12 text-gray-400" data-id="element-565" />}
              {selectedType === 'game' && <Gamepad2 className="w-12 h-12 text-gray-400" data-id="element-566" />}
              {selectedType === 'quest' && <Map className="w-12 h-12 text-gray-400" data-id="element-567" />}
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-568">
              {creationTypes.find(t => t.id === selectedType)?.name} Coming
              Soon
            </h2>
            <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto" data-id="element-569">
              We're working hard to bring you amazing new ways to transform your
              stories.
              {selectedType === 'movie' && ' Create animated videos with AI-generated scenes and voiceovers.'}
              {selectedType === 'game' && ' Build interactive story-driven games where players make choices.'}
              {selectedType === 'quest' && ' Design adventure journeys with challenges and discoveries.'}
            </p>
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl" data-id="element-570">
              <Lock className="w-5 h-5" data-id="element-571" />
              <span className="font-medium" data-id="element-572">Notify me when available</span>
            </div>
          </div>}
      </div>
    </div>;
}